import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuestionsFeed, useLeaderboard, useEnrollments, useClassComments, usePostComment, useDeleteComment, useActiveSessions, useCreateSession, useDeleteSession, useSessionHistory, useClassUploads, useClassFriendUploads, useFriends, type StudySession } from '../lib/api';
import type { ClassComment } from '@shared/types';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { deriveCategories, buildCategoryIndex } from '../lib/topic-tags';
import { getFlaggedQuestions } from '../lib/flagged-questions';
import { getReviewedFlashcardIds, getFlaggedFlashcardIds } from '../lib/flashcard-progress';

// Question source scope for the session pickers.
type QSource = 'all' | 'mine' | 'friends';

export function ClassPage() {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const { data: feed, isLoading: feedLoading } = useQuestionsFeed({ class_section_id: sectionId });
  const { data: lb } = useLeaderboard('class_section', sectionId ?? null);
  const { data: enrollData } = useEnrollments();
  const { data: commentsData } = useClassComments(sectionId);
  const { data: sessionsData } = useActiveSessions({ classSectionId: sectionId ?? null });
  const createSession = useCreateSession();
  const deleteSession = useDeleteSession();
  const postComment = usePostComment();
  const deleteComment = useDeleteComment();
  const { data: historyData } = useSessionHistory({ classSectionId: sectionId ?? null });
  const sessionHistory = historyData?.sessions ?? [];

  const enrollment = enrollData?.enrollments?.find((e: any) => e.id === sectionId);
  const questionCount = feed?.questions?.length ?? 0;
  const hasQuestions = questionCount > 0;
  const activeSessions = sessionsData?.sessions ?? [];

  const [showFullLb, setShowFullLb] = useState(false);
  const [showModePicker, setShowModePicker] = useState(false);
  const [showTopicPicker, setShowTopicPicker] = useState(false);
  const [showFriendsUpsell, setShowFriendsUpsell] = useState(false);
  const [sessionCount, setSessionCount] = useState(20);
  const [sessionName, setSessionName] = useState('');
  const [studyMode, setStudyMode] = useState<'questions' | 'flashcards' | 'quick'>('questions');
  const [questionFilter, setQuestionFilter] = useState<QSource>('all');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Uploads usable as session filters (under My Questions / Friends Q)
  const { data: uploadsData } = useClassUploads({ class_section_id: sectionId });
  const myUploads = uploadsData?.uploads ?? [];
  const { data: friendUploadsData } = useClassFriendUploads({ class_section_id: sectionId });
  const friendUploads = friendUploadsData?.uploads ?? [];
  const { data: friendsData } = useFriends();
  const friendIds = useMemo(
    () => new Set((friendsData?.friends ?? []).filter(f => f.status === 'accepted').map(f => f.id)),
    [friendsData],
  );
  const [selectedUploadIds, setSelectedUploadIds] = useState<Set<string>>(new Set());
  const toggleUpload = (id: string) => {
    setSelectedUploadIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Filter a pool by question source scope (mine / friends) and selected uploads.
  const applyScope = (pool: any[], scope: QSource): any[] => {
    let p = pool;
    if (scope === 'mine' && feed?.currentUserId) {
      p = p.filter((q: any) => q.authorUserId === feed.currentUserId);
    } else if (scope === 'friends') {
      p = p.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId));
    }
    if (scope !== 'all' && selectedUploadIds.size > 0) {
      p = p.filter((q: any) => q.ingestionJobId && selectedUploadIds.has(q.ingestionJobId));
    }
    return p;
  };

  // Entitlements from feed
  const canUseFlashcards = feed?.entitlements?.canUseFlashcards ?? false;
  const canUseFriendDecks = feed?.entitlements?.canUseFriendDecks ?? false;

  // All flashcard questions
  const allFlashcards = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type === 'flashcard'),
    [feed?.questions],
  );
  const hasFlashcards = allFlashcards.length > 0;

  // All quick memory questions
  const allQuickQuestions = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type === 'quick_mcq'),
    [feed?.questions],
  );
  const hasQuick = allQuickQuestions.length > 0;

  // Flashcard topic indexing (uses payload.topic as the category key)
  const fcCategoryIndex = useMemo(() => {
    const index = new Map<string, Set<string>>();
    for (const q of allFlashcards) {
      const topic = (q.payload as any)?.topic || 'General';
      let set = index.get(topic);
      if (!set) { set = new Set(); index.set(topic, set); }
      set.add(q.id);
    }
    return index;
  }, [allFlashcards]);

  const fcAvailableTopics = useMemo(() => {
    return Array.from(fcCategoryIndex.entries())
      .sort((a, b) => b[1].size - a[1].size)
      .map(([name, qIds]) => ({ name, count: qIds.size }));
  }, [fcCategoryIndex]);

  // Flashcard filter states
  const [fcFilter, setFcFilter] = useState<QSource>('all');
  const [fcSelectedTopics, setFcSelectedTopics] = useState<Set<string>>(new Set());
  const [fcUnansweredOnly, setFcUnansweredOnly] = useState(false);
  const [fcFlaggedOnly, setFcFlaggedOnly] = useState(false);
  const fcAllSelected = fcSelectedTopics.size === 0;

  const fcReviewedSet = useMemo(() => getReviewedFlashcardIds(), [allFlashcards]);
  const fcFlaggedSet = useMemo(() => getFlaggedFlashcardIds(), [allFlashcards]);

  const fcFilteredPool = useMemo(() => {
    let pool = applyScope(allFlashcards, fcFilter);
    if (!fcAllSelected) {
      const ids = new Set<string>();
      for (const cat of fcSelectedTopics) {
        const qIds = fcCategoryIndex.get(cat);
        if (qIds) qIds.forEach(id => ids.add(id));
      }
      pool = pool.filter(q => ids.has(q.id));
    }
    if (fcUnansweredOnly) pool = pool.filter(q => !fcReviewedSet.has(q.id));
    if (fcFlaggedOnly) pool = pool.filter(q => fcFlaggedSet.has(q.id));
    return pool;
  }, [allFlashcards, fcFilter, feed?.currentUserId, friendIds, fcAllSelected, fcSelectedTopics, fcCategoryIndex, fcUnansweredOnly, fcFlaggedOnly, fcReviewedSet, fcFlaggedSet, selectedUploadIds]);

  // Per-topic counts for flashcards (reactive to filters)
  const fcFilteredTopicCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const [name, qIds] of fcCategoryIndex.entries()) {
      let topicCards = applyScope(allFlashcards.filter(q => qIds.has(q.id)), fcFilter);
      if (fcUnansweredOnly) topicCards = topicCards.filter(q => !fcReviewedSet.has(q.id));
      if (fcFlaggedOnly) topicCards = topicCards.filter(q => fcFlaggedSet.has(q.id));
      counts.set(name, topicCards.length);
    }
    return counts;
  }, [fcCategoryIndex, allFlashcards, fcFilter, feed?.currentUserId, friendIds, fcUnansweredOnly, fcFlaggedOnly, fcReviewedSet, fcFlaggedSet, selectedUploadIds]);

  // Extract unique topics from questions (exclude flashcards + quick memory)
  const allStudyableQuestions = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type !== 'flashcard' && q.type !== 'quick_mcq'),
    [feed?.questions],
  );
  const studyableQuestions = useMemo(
    () => applyScope(allStudyableQuestions, questionFilter),
    [allStudyableQuestions, questionFilter, feed?.currentUserId, friendIds, selectedUploadIds],
  );

  const categoryIndex = useMemo(
    () => buildCategoryIndex(studyableQuestions),
    [studyableQuestions],
  );
  const availableTopics = useMemo(() => {
    return Array.from(categoryIndex.entries())
      .sort((a, b) => b[1].size - a[1].size)
      .map(([name, qIds]) => ({ name, count: qIds.size }));
  }, [categoryIndex]);

  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [answeredWrongOnly, setAnsweredWrongOnly] = useState(false);
  const allSelected = selectedTopics.size === 0; // empty = all

  const answeredSet = useMemo(
    () => new Set(feed?.answeredQuestionIds ?? []),
    [feed?.answeredQuestionIds],
  );
  const wrongSet = useMemo(
    () => new Set(feed?.wrongQuestionIds ?? []),
    [feed?.wrongQuestionIds],
  );

  // ── Quick Memory filter states ──────────────────────────────────────────
  const [qmFilter, setQmFilter] = useState<QSource>('all');
  const [qmSelectedTopics, setQmSelectedTopics] = useState<Set<string>>(new Set());
  const [qmUnansweredOnly, setQmUnansweredOnly] = useState(false);
  const [qmAnsweredWrongOnly, setQmAnsweredWrongOnly] = useState(false);
  const qmAllSelected = qmSelectedTopics.size === 0;

  // Quick topic indexing (uses payload.topic as the category key)
  const qmCategoryIndex = useMemo(() => {
    const index = new Map<string, Set<string>>();
    for (const q of allQuickQuestions) {
      const topic = (q.payload as any)?.topic || 'General';
      let set = index.get(topic);
      if (!set) { set = new Set(); index.set(topic, set); }
      set.add(q.id);
    }
    return index;
  }, [allQuickQuestions]);

  const qmAvailableTopics = useMemo(() => {
    return Array.from(qmCategoryIndex.entries())
      .sort((a, b) => b[1].size - a[1].size)
      .map(([name, qIds]) => ({ name, count: qIds.size }));
  }, [qmCategoryIndex]);

  // Quick pool BEFORE topic selection (mine/unanswered/wrong applied) — drives
  // per-topic counts; topic selection applied on top for the session pool.
  const qmBasePool = useMemo(() => {
    let pool = applyScope(allQuickQuestions, qmFilter);
    if (qmUnansweredOnly) pool = pool.filter(q => !answeredSet.has(q.id));
    if (qmAnsweredWrongOnly) pool = pool.filter(q => wrongSet.has(q.id));
    return pool;
  }, [allQuickQuestions, qmFilter, feed?.currentUserId, friendIds, selectedUploadIds, qmUnansweredOnly, qmAnsweredWrongOnly, answeredSet, wrongSet]);

  const qmFilteredPool = useMemo(() => {
    if (qmAllSelected) return qmBasePool;
    const ids = new Set<string>();
    for (const cat of qmSelectedTopics) {
      const qIds = qmCategoryIndex.get(cat);
      if (qIds) qIds.forEach(id => ids.add(id));
    }
    return qmBasePool.filter(q => ids.has(q.id));
  }, [qmBasePool, qmAllSelected, qmSelectedTopics, qmCategoryIndex]);

  const qmFilteredTopicCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const poolIds = new Set(qmBasePool.map(q => q.id));
    for (const [name, qIds] of qmCategoryIndex.entries()) {
      let n = 0;
      qIds.forEach(id => { if (poolIds.has(id)) n++; });
      counts.set(name, n);
    }
    return counts;
  }, [qmCategoryIndex, qmBasePool]);

  const handleStartStudy = () => {
    // Wait for the feed before deciding the picker path. Before it loads,
    // hasFlashcards/hasQuick/availableTopics are empty, which would skip the
    // mode/topic picker and drop the user straight into studying. (Bug: first
    // tap on a freshly-opened section bypassed the session-setup flow.)
    if (feedLoading) return;
    if (hasFlashcards || hasQuick) {
      setShowModePicker(true);
    } else {
      openQuestionTopicPicker();
    }
  };

  const openQuestionTopicPicker = () => {
    setShowModePicker(false);
    setStudyMode('questions');
    setSelectedTopics(new Set());
    setQuestionFilter('all');
    setUnansweredOnly(false);
    setAnsweredWrongOnly(false);
    setSelectedUploadIds(new Set());
    setSessionName('');
    if (availableTopics.length <= 1) {
      navigate(`/study/class/${sectionId}`);
      return;
    }
    setShowTopicPicker(true);
  };

  const openFlashcardTopicPicker = () => {
    setShowModePicker(false);
    setStudyMode('flashcards');
    setFcSelectedTopics(new Set());
    setFcFilter('all');
    setFcUnansweredOnly(false);
    setFcFlaggedOnly(false);
    setSelectedUploadIds(new Set());
    setSessionName('');
    setShowTopicPicker(true);
  };

  const openQuickTopicPicker = () => {
    setShowModePicker(false);
    setStudyMode('quick');
    setQmSelectedTopics(new Set());
    setQmFilter('all');
    setQmUnansweredOnly(false);
    setQmAnsweredWrongOnly(false);
    setSelectedUploadIds(new Set());
    setSessionName('');
    setShowTopicPicker(true);
  };

  const toggleTopic = (topic: string) => {
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topic)) next.delete(topic);
      else next.add(topic);
      return next;
    });
  };

  // Apply active filters to a question pool
  const applyFilters = (pool: typeof studyableQuestions) => {
    if (unansweredOnly) pool = pool.filter(q => !answeredSet.has(q.id));
    if (answeredWrongOnly) pool = pool.filter(q => wrongSet.has(q.id));
    return pool;
  };

  // Compute how many unique questions are matched by current selection
  const selectedQuestionCount = useMemo(() => {
    let pool = studyableQuestions;
    if (!allSelected) {
      const ids = new Set<string>();
      for (const cat of selectedTopics) {
        const qIds = categoryIndex.get(cat);
        if (qIds) qIds.forEach((id) => ids.add(id));
      }
      pool = pool.filter(q => ids.has(q.id));
    }
    pool = applyFilters(pool);
    return pool.length;
  }, [allSelected, selectedTopics, categoryIndex, studyableQuestions, unansweredOnly, answeredWrongOnly, answeredSet, wrongSet]);

  // Per-topic filtered counts (reactive to unanswered/wrong toggles)
  const filteredTopicCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const [name, qIds] of categoryIndex.entries()) {
      const topicQuestions = studyableQuestions.filter(q => qIds.has(q.id));
      counts.set(name, applyFilters(topicQuestions).length);
    }
    return counts;
  }, [categoryIndex, studyableQuestions, unansweredOnly, answeredWrongOnly, answeredSet, wrongSet]);

  const getFilteredQuestionIds = (): string[] => {
    let pool = studyableQuestions;
    if (!allSelected) {
      const ids = new Set<string>();
      for (const cat of selectedTopics) {
        const qIds = categoryIndex.get(cat);
        if (qIds) qIds.forEach(id => ids.add(id));
      }
      pool = pool.filter(q => ids.has(q.id));
    }
    pool = applyFilters(pool);
    return pool.map(q => q.id);
  };

  // Optional user-given session name + de-duplication so open sessions are
  // distinguishable ("Cardiovascular", "Cardiovascular (2)", ...).
  const uniqueSessionLabel = (base: string): string => {
    const name = base.trim() || 'Study Session';
    const taken = new Set(activeSessions.map(s => s.label).filter(Boolean));
    if (!taken.has(name)) return name;
    let n = 2;
    while (taken.has(`${name} (${n})`)) n++;
    return `${name} (${n})`;
  };

  // Live default labels, used as both the name-input placeholder and the
  // fallback when the user leaves the name blank.
  const scopeLabel = (scope: QSource, kind: string): string =>
    scope === 'friends' ? `Friends' ${kind}` : scope === 'mine' ? `My ${kind}` : `All ${kind}`;

  const questionsDefaultLabel = useMemo(() => {
    const topicNames = allSelected ? [] : Array.from(selectedTopics);
    const prefix = questionFilter === 'friends' ? "Friends: " : '';
    return allSelected
      ? scopeLabel(questionFilter, 'Topics')
      : prefix + topicNames.slice(0, 3).join(', ') + (topicNames.length > 3 ? ` +${topicNames.length - 3}` : '');
  }, [allSelected, selectedTopics, questionFilter, selectedUploadIds]);

  const quickDefaultLabel = useMemo(() => {
    const topicNames = qmAllSelected ? [] : Array.from(qmSelectedTopics);
    const prefix = qmFilter === 'friends' ? "Friends: " : '';
    return qmAllSelected
      ? (qmFilter === 'all' ? 'Quick Study' : scopeLabel(qmFilter, 'Quick'))
      : prefix + topicNames.slice(0, 3).join(', ') + (topicNames.length > 3 ? ` +${topicNames.length - 3}` : '');
  }, [qmAllSelected, qmSelectedTopics, qmFilter, selectedUploadIds]);

  const flashDefaultLabel = useMemo(() => {
    const topicNames = fcAllSelected ? [] : Array.from(fcSelectedTopics);
    const prefix = fcFilter === 'friends' ? "Friends: " : '';
    return fcAllSelected
      ? (fcFilter === 'all' ? 'All Flash Cards' : scopeLabel(fcFilter, 'Cards'))
      : prefix + topicNames.slice(0, 3).join(', ') + (topicNames.length > 3 ? ` +${topicNames.length - 3}` : '');
  }, [fcAllSelected, fcSelectedTopics, fcFilter, selectedUploadIds]);

  const confirmTopics = async () => {
    setShowTopicPicker(false);
    const allIds = getFilteredQuestionIds();
    if (allIds.length === 0) return;

    // Shuffle, then take the user's chosen count (capped at what's available)
    // so each session is a fresh sample of the selected topics.
    for (let i = allIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allIds[i], allIds[j]] = [allIds[j]!, allIds[i]!];
    }
    const count = Math.min(Math.max(1, sessionCount), allIds.length);
    const questionIds = allIds.slice(0, count);

    const topicNames = allSelected ? [] : Array.from(selectedTopics);
    const uploadIds = questionFilter !== 'all' && selectedUploadIds.size > 0 ? Array.from(selectedUploadIds) : undefined;
    const label = uniqueSessionLabel(sessionName || questionsDefaultLabel);

    const result = await createSession.mutateAsync({
      classSectionId: sectionId,
      filters: allSelected && !uploadIds ? null : { topics: topicNames, ...(uploadIds ? { uploadIds } : {}) },
      questionIds,
      label,
    });

    navigate(`/study/class/${sectionId}?session=${result.session.id}`);
  };

  const confirmQuickTopics = async () => {
    setShowTopicPicker(false);
    const allIds = qmFilteredPool.map(q => q.id);
    if (allIds.length === 0) return;

    // Shuffle, then take the user's chosen count (capped at what's available).
    for (let i = allIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allIds[i], allIds[j]] = [allIds[j]!, allIds[i]!];
    }
    const count = Math.min(Math.max(1, sessionCount), allIds.length);
    const questionIds = allIds.slice(0, count);

    const topicNames = qmAllSelected ? [] : Array.from(qmSelectedTopics);
    const uploadIds = qmFilter !== 'all' && selectedUploadIds.size > 0 ? Array.from(selectedUploadIds) : undefined;
    const label = uniqueSessionLabel(sessionName || quickDefaultLabel);

    const result = await createSession.mutateAsync({
      classSectionId: sectionId,
      filters: { topics: topicNames, mode: 'quick', ...(uploadIds ? { uploadIds } : {}) },
      questionIds,
      label,
    });

    navigate(`/quickmem/${sectionId}?session=${result.session.id}`);
  };

  const confirmFlashcardTopics = async () => {
    setShowTopicPicker(false);
    const questionIds = fcFilteredPool.map(q => q.id);
    if (questionIds.length === 0) return;

    // Shuffle for variety
    for (let i = questionIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = questionIds[i]!;
      questionIds[i] = questionIds[j]!;
      questionIds[j] = tmp;
    }

    const topicNames = fcAllSelected ? [] : Array.from(fcSelectedTopics);
    const fcUploadIds = fcFilter !== 'all' && selectedUploadIds.size > 0 ? Array.from(selectedUploadIds) : undefined;
    const label = uniqueSessionLabel(sessionName || flashDefaultLabel);

    const result = await createSession.mutateAsync({
      classSectionId: sectionId,
      filters: { topics: topicNames, mode: 'flashcard', ...(fcUploadIds ? { uploadIds: fcUploadIds } : {}) } as any,
      questionIds,
      label,
    });

    navigate(`/flashcards/${sectionId}?session=${result.session.id}`);
  };

  const resumeSession = (session: StudySession) => {
    const mode = (session.filters as any)?.mode;
    if (mode === 'flashcard') {
      navigate(`/flashcards/${sectionId}?session=${session.id}`);
    } else if (mode === 'quick') {
      navigate(`/quickmem/${sectionId}?session=${session.id}`);
    } else {
      navigate(`/study/class/${sectionId}?session=${session.id}`);
    }
  };

  // Full leaderboard overlay
  if (showFullLb) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <button
            onClick={() => setShowFullLb(false)}
            className="flex items-center gap-1 text-squishy-primary font-medium mb-4 text-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Class
          </button>
          <h1 className="text-xl font-bold text-squishy-dark mb-1">Class Leaderboard</h1>
          <p className="text-xs text-slate-500 mb-4">Rolling 30 days</p>
          <ol className="bg-white rounded-xl border divide-y">
            {lb?.entries.map((e) => (
              <li key={e.userId} className={`flex items-center gap-3 p-3 ${e.isCurrentUser ? 'bg-teal-50' : ''}`}>
                <div className="w-8 text-right font-mono text-sm text-slate-500">{e.rank}</div>
                <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-xs font-medium">
                  {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
                </div>
                <div className="flex-1 font-medium text-sm inline-flex items-center gap-1 min-w-0">
                  <span className="truncate">{e.displayName}</span>
                  {e.verifiedStudent && <VerifiedBadge />}
                </div>
                <div className="text-xs text-slate-500">L{e.level}</div>
                <div className="text-sm font-mono text-squishy-dark w-20 text-right">{e.xp.toLocaleString()} XP</div>
              </li>
            ))}
            {(!lb?.entries || lb.entries.length === 0) && (
              <li className="p-6 text-center text-slate-400 text-sm">No activity in the last 30 days</li>
            )}
          </ol>
          {lb?.currentUserRank && (
            <p className="text-xs text-slate-500 mt-3">Your rank: #{lb.currentUserRank} of {lb.totalUsers}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-lg mx-auto">
      {/* Header + inline stats */}
      <div className="flex items-center gap-2 pt-1">
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-squishy-dark truncate">
            {enrollment?.class_code ?? 'Class'}
          </h1>
          {enrollment && (
            <div className="text-[11px] text-slate-400 truncate">
              {enrollment.professor_name ? `Prof. ${enrollment.professor_name}` : enrollment.school_name}
              {enrollment.term ? ` · ${enrollment.term}` : ''}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500 shrink-0">
          <div className="text-center">
            <span className="font-semibold text-squishy-primary">{allStudyableQuestions.length}</span>
            <span className="text-slate-300 ml-0.5">Q</span>
          </div>
          {hasFlashcards && (
            <div className="text-center">
              <span className="font-semibold text-violet-500">{allFlashcards.length}</span>
              <span className="text-slate-300 ml-0.5">FC</span>
            </div>
          )}
          {hasQuick && (
            <div className="text-center">
              <span className="font-semibold text-amber-500">{allQuickQuestions.length}</span>
              <span className="text-slate-300 ml-0.5">QS</span>
            </div>
          )}
        </div>
      </div>

      {/* Primary CTA */}
      {!feedLoading && !hasQuestions ? (
        <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-6 text-center space-y-2">
          <h2 className="text-sm font-semibold text-squishy-dark">No questions yet</h2>
          <p className="text-slate-500 text-xs">Upload notes or slides to generate practice questions.</p>
          <Link to={`/upload?class=${sectionId}`}
                className="inline-flex items-center gap-2 bg-squishy-primary text-white font-semibold px-5 py-2.5 rounded-lg hover:opacity-90 transition text-sm">
            Upload Materials
          </Link>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={handleStartStudy}
            disabled={feedLoading}
            className="flex-1 bg-squishy-primary text-white text-center py-3 rounded-xl font-bold text-sm hover:opacity-90 transition active:scale-[0.97] disabled:opacity-60"
          >
            {feedLoading ? 'Loading…' : 'Start Studying'}
          </button>
          <Link to={`/upload?class=${sectionId}`}
                className="bg-white border border-squishy-primary text-squishy-primary px-3.5 py-3 rounded-xl font-semibold hover:bg-squishy-primary/5 transition text-sm">
            + Add
          </Link>
        </div>
      )}

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Continue Studying</h2>
          {activeSessions.map(session => {
            const mode = (session.filters as any)?.mode;
            return (
              <ClassSessionCard
                key={session.id}
                session={session}
                isFlashcard={mode === 'flashcard'}
                isQuick={mode === 'quick'}
                onResume={() => resumeSession(session)}
                onDelete={() => setConfirmDeleteId(session.id)}
              />
            );
          })}
        </div>
      )}

      {/* Marked for Review (Questions) */}
      <ClassMarkedForReviewCard
        allQuestions={allStudyableQuestions}
        createSession={createSession}
        navigate={navigate}
        sectionId={sectionId!}
      />

      {/* Marked for Review (Flashcards) */}
      <FlashcardReviewCard
        allFlashcards={allFlashcards}
        createSession={createSession}
        navigate={navigate}
        sectionId={sectionId!}
      />

      {/* Past Sessions */}
      {sessionHistory.length > 0 && (
        <ClassPastSessionsSection sessions={sessionHistory} />
      )}

      {/* Leaderboard — top 5 */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <h2 className="font-semibold text-squishy-dark text-sm">Leaderboard</h2>
          <span className="text-[10px] text-slate-400 uppercase tracking-wide">30 day</span>
        </div>
        <ol className="divide-y">
          {lb?.entries.slice(0, 5).map((e) => (
            <li key={e.userId} className={`flex items-center gap-2.5 px-4 py-2.5 ${e.isCurrentUser ? 'bg-teal-50' : ''}`}>
              <span className="w-5 text-right text-xs font-mono text-slate-400">{e.rank}</span>
              <div className="w-7 h-7 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-[10px] font-medium shrink-0">
                {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
              </div>
              <span className="flex-1 text-sm font-medium truncate inline-flex items-center gap-1">
                <span className="truncate">{e.displayName}</span>
                {e.verifiedStudent && <VerifiedBadge />}
              </span>
              <span className="text-xs font-mono text-squishy-dark">{e.xp.toLocaleString()} XP</span>
            </li>
          ))}
          {(!lb?.entries || lb.entries.length === 0) && (
            <li className="px-4 py-6 text-center text-slate-400 text-sm">No activity yet</li>
          )}
        </ol>
        {lb && lb.entries.length > 5 && (
          <button
            onClick={() => setShowFullLb(true)}
            className="w-full py-2.5 text-center text-squishy-primary text-sm font-medium border-t hover:bg-slate-50 transition"
          >
            View Full Leaderboard
          </button>
        )}
        {lb && lb.entries.length > 0 && lb.entries.length <= 5 && (
          <button
            onClick={() => setShowFullLb(true)}
            className="w-full py-2.5 text-center text-squishy-primary text-sm font-medium border-t hover:bg-slate-50 transition"
          >
            View All
          </button>
        )}
      </div>

      {/* Comments */}
      <CommentsSection
        classSectionId={sectionId!}
        comments={commentsData?.comments ?? []}
        entitlements={commentsData?.entitlements}
        postComment={postComment}
        deleteComment={deleteComment}
      />

      {/* Friends' decks upsell (Plus+) */}
      {showFriendsUpsell && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => setShowFriendsUpsell(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white rounded-2xl w-full max-w-sm p-5 text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-amber-500">
                <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-squishy-dark">Study your friends' decks</h3>
            <p className="mt-2 text-sm text-slate-600">
              Practice the questions, flashcards, and quick sets your friends uploaded. Studying friends' decks is a <span className="font-semibold">Plus &amp; Pro</span> feature.
            </p>
            <Link
              to="/profile"
              onClick={() => setShowFriendsUpsell(false)}
              className="mt-5 block w-full rounded-xl bg-squishy-primary py-3 text-sm font-semibold text-white"
            >
              Upgrade to Plus (from $1.99/mo)
            </Link>
            <button onClick={() => setShowFriendsUpsell(false)} className="mt-2 w-full py-2 text-sm text-slate-400">
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Mode Picker Modal */}
      {showModePicker && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModePicker(false)} />
          <div className="relative bg-white w-full max-w-sm rounded-t-2xl sm:rounded-2xl overflow-hidden animate-slide-up">
            <div className="px-5 pt-5 pb-3 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-squishy-dark">Create Study Session</h2>
                <button onClick={() => setShowModePicker(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Choose what you want to study.</p>
            </div>
            <div className="p-5 space-y-3">
              <button
                onClick={openQuestionTopicPicker}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-squishy-primary hover:bg-teal-50 transition-all active:scale-[0.98]"
              >
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-squishy-primary">
                    <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97zM6.75 8.25a.75.75 0 01.75-.75h9a.75.75 0 010 1.5h-9a.75.75 0 01-.75-.75zm.75 3.75a.75.75 0 000 1.5h9a.75.75 0 000-1.5h-9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold text-squishy-dark">Questions</div>
                  <div className="text-xs text-slate-500">{allStudyableQuestions.length} questions available</div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                onClick={canUseFlashcards ? openFlashcardTopicPicker : undefined}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${canUseFlashcards ? 'border-slate-200 hover:border-violet-400 hover:bg-violet-50 active:scale-[0.98]' : 'border-slate-100 bg-slate-50 opacity-75'}`}
              >
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-violet-500">
                    <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold text-squishy-dark">Flash Cards</div>
                  {canUseFlashcards
                    ? <div className="text-xs text-slate-500">{allFlashcards.length} cards available</div>
                    : <div className="text-xs text-violet-500 font-medium">Upgrade to Plus to study flash cards</div>
                  }
                </div>
                {canUseFlashcards ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span className="text-[10px] font-bold text-violet-500 bg-violet-100 dark:bg-violet-500/20 px-2 py-0.5 rounded-md">PLUS</span>
                )}
              </button>
              <button
                onClick={hasQuick ? openQuickTopicPicker : undefined}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${hasQuick ? 'border-slate-200 hover:border-amber-400 hover:bg-amber-50 active:scale-[0.98]' : 'border-slate-100 bg-slate-50 opacity-75'}`}
              >
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-amber-500">
                    <path fillRule="evenodd" d="M14.615 1.595a.75.75 0 01.359.852L12.982 9.75h7.268a.75.75 0 01.548 1.262l-10.5 11.25a.75.75 0 01-1.272-.71l1.992-7.302H3.75a.75.75 0 01-.548-1.262l10.5-11.25a.75.75 0 01.913-.143z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold text-squishy-dark">Quick Study</div>
                  {hasQuick
                    ? <div className="text-xs text-slate-500">{allQuickQuestions.length} rapid-fire questions</div>
                    : <div className="text-xs text-slate-400">Upload material to generate quick questions</div>
                  }
                </div>
                {hasQuick && (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topic Picker Modal (Questions or Flash Cards) */}
      {showTopicPicker && studyMode === 'questions' && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowTopicPicker(false)} />
          <div className="relative bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col animate-slide-up">
            <div className="px-5 pt-5 pb-3 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-squishy-dark">Choose Topics</h2>
                <button onClick={() => setShowTopicPicker(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Select which topics to study, or keep All selected.</p>
              {/* Source: All / My Questions / Friends Q */}
              <SourceTabs
                value={questionFilter}
                onChange={(s) => { setQuestionFilter(s); setSelectedTopics(new Set()); setSelectedUploadIds(new Set()); }}
                counts={{
                  all: allStudyableQuestions.length,
                  mine: allStudyableQuestions.filter((q: any) => q.authorUserId === feed?.currentUserId).length,
                  friends: allStudyableQuestions.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId)).length,
                }}
                accent="text-squishy-primary"
                mineLabel="My Q"
                friendsLabel="Friends Q"
                friendsLocked={!canUseFriendDecks}
                onLockedFriends={() => setShowFriendsUpsell(true)}
              />
              {questionFilter !== 'all' && (
                <UploadFilterChips
                  uploads={questionFilter === 'friends' ? friendUploads : myUploads}
                  selected={selectedUploadIds}
                  onToggle={toggleUpload}
                  countFor={(u) => u.questionCount}
                  showAuthor={questionFilter === 'friends'}
                  accentClass="border-squishy-primary bg-teal-50 text-squishy-primary"
                />
              )}
              {/* Unanswered Only toggle */}
              <label className="mt-3 flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Unanswered only</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({studyableQuestions.filter(q => !answeredSet.has(q.id)).length} new)
                  </span>
                </div>
                <div
                  onClick={() => { setUnansweredOnly(v => !v); setAnsweredWrongOnly(false); }}
                  className={`relative rounded-full transition-colors ${unansweredOnly ? 'bg-squishy-primary' : 'bg-slate-300'}`}
                  style={{ width: 40, height: 22 }}
                >
                  <div
                    className="absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform"
                    style={{ width: 18, height: 18, transform: unansweredOnly ? 'translateX(18px)' : 'translateX(0)' }}
                  />
                </div>
              </label>
              {/* Answered Wrong Only toggle */}
              <label className="mt-2 flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Answered wrong only</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({wrongSet.size} to retry)
                  </span>
                </div>
                <div
                  onClick={() => { setAnsweredWrongOnly(v => !v); setUnansweredOnly(false); }}
                  className={`relative rounded-full transition-colors ${answeredWrongOnly ? 'bg-red-500' : 'bg-slate-300'}`}
                  style={{ width: 40, height: 22 }}
                >
                  <div
                    className="absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform"
                    style={{ width: 18, height: 18, transform: answeredWrongOnly ? 'translateX(18px)' : 'translateX(0)' }}
                  />
                </div>
              </label>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {/* All option */}
              <button
                onClick={() => setSelectedTopics(new Set())}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                  allSelected
                    ? 'border-squishy-primary bg-teal-50 text-squishy-primary'
                    : 'border-slate-200 text-slate-600'
                }`}
              >
                <span className="font-medium">All Topics</span>
                <span className="text-xs text-slate-400">{applyFilters(studyableQuestions).length} questions</span>
              </button>

              {/* Individual topics */}
              {availableTopics.map((t) => {
                const isSelected = selectedTopics.has(t.name);
                const filteredCount = filteredTopicCounts.get(t.name) ?? t.count;
                return (
                  <button
                    key={t.name}
                    onClick={() => toggleTopic(t.name)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                      isSelected
                        ? 'border-squishy-primary bg-teal-50 text-squishy-primary'
                        : 'border-slate-200 text-slate-600'
                    }`}
                  >
                    <span className="font-medium text-left">{t.name}</span>
                    <span className="text-xs text-slate-400 shrink-0 ml-2">{filteredCount}</span>
                  </button>
                );
              })}
            </div>

            <div className="px-5 py-4 border-t space-y-3">
              <SessionNameField value={sessionName} onChange={setSessionName} placeholder={questionsDefaultLabel || 'e.g. Cardio review'} accent="focus:ring-squishy-primary/30" />
              {/* How many questions this session */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-squishy-dark">Questions this session</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={selectedQuestionCount}
                    value={Math.min(sessionCount, selectedQuestionCount) || ''}
                    onChange={(e) => setSessionCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-center text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-squishy-primary/30"
                  />
                  <span className="text-xs text-slate-400">/ {selectedQuestionCount} max</span>
                  <button
                    onClick={() => setSessionCount(selectedQuestionCount)}
                    className="text-xs font-semibold text-squishy-primary hover:underline"
                  >
                    Max
                  </button>
                </div>
              </div>
              <button
                onClick={confirmTopics}
                disabled={selectedQuestionCount === 0}
                className="w-full bg-squishy-primary text-white py-3.5 rounded-xl font-semibold text-base hover:opacity-90 transition active:scale-[0.98] disabled:opacity-40"
              >
                Start Studying : {Math.min(Math.max(1, sessionCount), selectedQuestionCount)} question{Math.min(Math.max(1, sessionCount), selectedQuestionCount) !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flash Card Topic Picker Modal */}
      {showTopicPicker && studyMode === 'flashcards' && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowTopicPicker(false)} />
          <div className="relative bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col animate-slide-up">
            <div className="px-5 pt-5 pb-3 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-squishy-dark">Flash Card Topics</h2>
                <button onClick={() => setShowTopicPicker(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Select topics or keep All selected.</p>
              {/* Source: All / My Cards / Friends */}
              <SourceTabs
                value={fcFilter}
                onChange={(s) => { setFcFilter(s); setFcSelectedTopics(new Set()); setSelectedUploadIds(new Set()); }}
                counts={{
                  all: allFlashcards.length,
                  mine: allFlashcards.filter((q: any) => q.authorUserId === feed?.currentUserId).length,
                  friends: allFlashcards.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId)).length,
                }}
                accent="text-violet-600"
                mineLabel="My"
                friendsLabel="Friends"
                friendsLocked={!canUseFriendDecks}
                onLockedFriends={() => setShowFriendsUpsell(true)}
              />
              {fcFilter !== 'all' && (
                <UploadFilterChips
                  uploads={fcFilter === 'friends' ? friendUploads : myUploads}
                  selected={selectedUploadIds}
                  onToggle={toggleUpload}
                  countFor={(u) => u.flashcardCount}
                  showAuthor={fcFilter === 'friends'}
                  accentClass="border-violet-400 bg-violet-50 text-violet-600"
                />
              )}
              {/* Unseen Only toggle */}
              <label className="mt-3 flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Unseen only</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({allFlashcards.filter(q => !fcReviewedSet.has(q.id)).length} new)
                  </span>
                </div>
                <div
                  onClick={() => { setFcUnansweredOnly(v => !v); setFcFlaggedOnly(false); }}
                  className={`relative rounded-full transition-colors ${fcUnansweredOnly ? 'bg-violet-500' : 'bg-slate-300'}`}
                  style={{ width: 40, height: 22 }}
                >
                  <div
                    className="absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform"
                    style={{ width: 18, height: 18, transform: fcUnansweredOnly ? 'translateX(18px)' : 'translateX(0)' }}
                  />
                </div>
              </label>
              {/* Marked for Review Only toggle */}
              <label className="mt-2 flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Marked for review</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({allFlashcards.filter(q => fcFlaggedSet.has(q.id)).length} flagged)
                  </span>
                </div>
                <div
                  onClick={() => { setFcFlaggedOnly(v => !v); setFcUnansweredOnly(false); }}
                  className={`relative rounded-full transition-colors ${fcFlaggedOnly ? 'bg-amber-500' : 'bg-slate-300'}`}
                  style={{ width: 40, height: 22 }}
                >
                  <div
                    className="absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform"
                    style={{ width: 18, height: 18, transform: fcFlaggedOnly ? 'translateX(18px)' : 'translateX(0)' }}
                  />
                </div>
              </label>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {/* All option */}
              <button
                onClick={() => setFcSelectedTopics(new Set())}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                  fcAllSelected
                    ? 'border-violet-400 bg-violet-50 text-violet-600'
                    : 'border-slate-200 text-slate-600'
                }`}
              >
                <span className="font-medium">All Topics</span>
                <span className="text-xs text-slate-400">{fcFilteredPool.length} cards</span>
              </button>

              {/* Individual topics */}
              {fcAvailableTopics.map((t) => {
                const isSelected = fcSelectedTopics.has(t.name);
                const filteredCount = fcFilteredTopicCounts.get(t.name) ?? t.count;
                return (
                  <button
                    key={t.name}
                    onClick={() => {
                      setFcSelectedTopics(prev => {
                        const next = new Set(prev);
                        if (next.has(t.name)) next.delete(t.name);
                        else next.add(t.name);
                        return next;
                      });
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                      isSelected
                        ? 'border-violet-400 bg-violet-50 text-violet-600'
                        : 'border-slate-200 text-slate-600'
                    }`}
                  >
                    <span className="font-medium text-left">{t.name}</span>
                    <span className="text-xs text-slate-400 shrink-0 ml-2">{filteredCount}</span>
                  </button>
                );
              })}
            </div>

            <div className="px-5 py-4 border-t space-y-3">
              <SessionNameField value={sessionName} onChange={setSessionName} placeholder={flashDefaultLabel || 'e.g. Drug cards'} accent="focus:ring-violet-400/30" />
              <button
                onClick={confirmFlashcardTopics}
                disabled={fcFilteredPool.length === 0}
                className="w-full bg-violet-500 text-white py-3.5 rounded-xl font-semibold text-base hover:opacity-90 transition active:scale-[0.98] disabled:opacity-40"
              >
                Start Flash Cards{!fcAllSelected ? ` : ${fcFilteredPool.length} card${fcFilteredPool.length !== 1 ? 's' : ''}` : ` (${fcFilteredPool.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Memory Topic Picker Modal */}
      {showTopicPicker && studyMode === 'quick' && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowTopicPicker(false)} />
          <div className="relative bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col animate-slide-up">
            <div className="px-5 pt-5 pb-3 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-squishy-dark">Quick Study</h2>
                <button onClick={() => setShowTopicPicker(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Rapid-fire recall. Tap an answer, see right or wrong, move on.</p>
              {/* Source: All / My Questions / Friends Q */}
              <SourceTabs
                value={qmFilter}
                onChange={(s) => { setQmFilter(s); setQmSelectedTopics(new Set()); setSelectedUploadIds(new Set()); }}
                counts={{
                  all: allQuickQuestions.length,
                  mine: allQuickQuestions.filter((q: any) => q.authorUserId === feed?.currentUserId).length,
                  friends: allQuickQuestions.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId)).length,
                }}
                accent="text-amber-600"
                mineLabel="My Q"
                friendsLabel="Friends Q"
                friendsLocked={!canUseFriendDecks}
                onLockedFriends={() => setShowFriendsUpsell(true)}
              />
              {qmFilter !== 'all' && (
                <UploadFilterChips
                  uploads={qmFilter === 'friends' ? friendUploads : myUploads}
                  selected={selectedUploadIds}
                  onToggle={toggleUpload}
                  countFor={(u) => u.quickCount}
                  showAuthor={qmFilter === 'friends'}
                  accentClass="border-amber-400 bg-amber-50 text-amber-600"
                />
              )}
              {/* Unanswered Only toggle */}
              <label className="mt-3 flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Unanswered only</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({allQuickQuestions.filter(q => !answeredSet.has(q.id)).length} new)
                  </span>
                </div>
                <div
                  onClick={() => { setQmUnansweredOnly(v => !v); setQmAnsweredWrongOnly(false); }}
                  className={`relative rounded-full transition-colors ${qmUnansweredOnly ? 'bg-amber-500' : 'bg-slate-300'}`}
                  style={{ width: 40, height: 22 }}
                >
                  <div
                    className="absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform"
                    style={{ width: 18, height: 18, transform: qmUnansweredOnly ? 'translateX(18px)' : 'translateX(0)' }}
                  />
                </div>
              </label>
              {/* Answered Wrong Only toggle */}
              <label className="mt-2 flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Answered wrong only</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({allQuickQuestions.filter(q => wrongSet.has(q.id)).length} to retry)
                  </span>
                </div>
                <div
                  onClick={() => { setQmAnsweredWrongOnly(v => !v); setQmUnansweredOnly(false); }}
                  className={`relative rounded-full transition-colors ${qmAnsweredWrongOnly ? 'bg-red-500' : 'bg-slate-300'}`}
                  style={{ width: 40, height: 22 }}
                >
                  <div
                    className="absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform"
                    style={{ width: 18, height: 18, transform: qmAnsweredWrongOnly ? 'translateX(18px)' : 'translateX(0)' }}
                  />
                </div>
              </label>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {/* All option */}
              <button
                onClick={() => setQmSelectedTopics(new Set())}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                  qmAllSelected
                    ? 'border-amber-400 bg-amber-50 text-amber-600'
                    : 'border-slate-200 text-slate-600'
                }`}
              >
                <span className="font-medium">All Topics</span>
                <span className="text-xs text-slate-400">{qmBasePool.length} questions</span>
              </button>

              {/* Individual topics */}
              {qmAvailableTopics.map((t) => {
                const isSelected = qmSelectedTopics.has(t.name);
                const filteredCount = qmFilteredTopicCounts.get(t.name) ?? t.count;
                return (
                  <button
                    key={t.name}
                    onClick={() => {
                      setQmSelectedTopics(prev => {
                        const next = new Set(prev);
                        if (next.has(t.name)) next.delete(t.name);
                        else next.add(t.name);
                        return next;
                      });
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                      isSelected
                        ? 'border-amber-400 bg-amber-50 text-amber-600'
                        : 'border-slate-200 text-slate-600'
                    }`}
                  >
                    <span className="font-medium text-left">{t.name}</span>
                    <span className="text-xs text-slate-400 shrink-0 ml-2">{filteredCount}</span>
                  </button>
                );
              })}
            </div>

            <div className="px-5 py-4 border-t space-y-3">
              <SessionNameField value={sessionName} onChange={setSessionName} placeholder={quickDefaultLabel || 'e.g. Lab values'} accent="focus:ring-amber-400/30" />
              {/* How many questions this session */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-squishy-dark">Questions this session</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={qmFilteredPool.length}
                    value={Math.min(sessionCount, qmFilteredPool.length) || ''}
                    onChange={(e) => setSessionCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-center text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                  />
                  <span className="text-xs text-slate-400">/ {qmFilteredPool.length} max</span>
                  <button
                    onClick={() => setSessionCount(qmFilteredPool.length)}
                    className="text-xs font-semibold text-amber-600 hover:underline"
                  >
                    Max
                  </button>
                </div>
              </div>
              <button
                onClick={confirmQuickTopics}
                disabled={qmFilteredPool.length === 0}
                className="w-full bg-amber-500 text-white py-3.5 rounded-xl font-semibold text-base hover:opacity-90 transition active:scale-[0.98] disabled:opacity-40"
              >
                Start Quick Study : {Math.min(Math.max(1, sessionCount), qmFilteredPool.length)} question{Math.min(Math.max(1, sessionCount), qmFilteredPool.length) !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Session Confirmation ─────────────────────────── */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6 animate-fade-in">
          <div className="bg-white rounded-2xl p-5 w-full max-w-xs shadow-xl text-center space-y-4 animate-slide-up">
            <div className="text-3xl">&#x1F5D1;</div>
            <p className="text-sm font-semibold text-squishy-navy">Discard this session?</p>
            <p className="text-xs text-slate-500">Your progress will be lost and can't be recovered.</p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition active:scale-[0.97]"
              >
                Keep
              </button>
              <button
                onClick={() => { deleteSession.mutate(confirmDeleteId); setConfirmDeleteId(null); }}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition active:scale-[0.97]"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Marked for Review ─────────────────────────────────────────────────────────

function ClassMarkedForReviewCard({ allQuestions, createSession, navigate, sectionId }: {
  allQuestions: any[];
  createSession: ReturnType<typeof useCreateSession>;
  navigate: ReturnType<typeof useNavigate>;
  sectionId: string;
}) {
  const flagged = getFlaggedQuestions();
  const questionIds = allQuestions.map(q => q.id);
  const flaggedInContext = flagged.filter(id => questionIds.includes(id));

  if (flaggedInContext.length === 0) return null;

  const handleStudyFlagged = async () => {
    const result = await createSession.mutateAsync({
      classSectionId: sectionId,
      questionIds: flaggedInContext,
      label: 'Marked for Review',
    });
    navigate(`/study/class/${sectionId}?session=${result.session.id}`);
  };

  return (
    <button
      onClick={handleStudyFlagged}
      disabled={createSession.isPending}
      className="w-full bg-white border border-amber-200 rounded-2xl p-4 flex items-center gap-3 active:bg-amber-50 transition-colors disabled:opacity-50"
    >
      <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-amber-400">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      </div>
      <div className="flex-1 text-left">
        <div className="text-sm font-semibold text-squishy-dark">Marked for Review</div>
        <div className="text-xs text-slate-500">{flaggedInContext.length} question{flaggedInContext.length !== 1 ? 's' : ''} flagged</div>
      </div>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400">
        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
      </svg>
    </button>
  );
}

// ── Past Sessions ─────────────────────────────────────────────────────────────

function ClassPastSessionsSection({ sessions }: { sessions: StudySession[] }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white border rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
      >
        <h2 className="font-semibold text-squishy-dark text-sm">Past Sessions</h2>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400">{sessions.length} completed</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
            className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          >
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </div>
      </button>
      {expanded && (
        <div className="divide-y">
          {sessions.map(s => {
            const accuracy = s.questionsAnswered > 0 ? Math.round((s.correctCount / s.questionsAnswered) * 100) : 0;
            const date = s.endedAt ? new Date(s.endedAt).toLocaleDateString() : new Date(s.startedAt).toLocaleDateString();

            return (
              <div key={s.id} className="px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-squishy-dark truncate">{s.label || 'All Topics'}</div>
                  <div className="flex items-center gap-3 text-[10px] text-slate-400 mt-0.5">
                    <span>{date}</span>
                    <span>{'\u00B7'}</span>
                    <span>{s.questionsAnswered} answered</span>
                    <span>{'\u00B7'}</span>
                    <span className={accuracy >= 70 ? 'text-green-600' : accuracy >= 50 ? 'text-amber-500' : 'text-red-500'}>{accuracy}%</span>
                    <span>{'\u00B7'}</span>
                    <span className="text-emerald-600">+{s.xpEarned} XP</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Session Card ──────────────────────────────────────────────────────────────

function ClassSessionCard({ session, isFlashcard, isQuick, onResume, onDelete }: {
  session: StudySession;
  isFlashcard?: boolean;
  isQuick?: boolean;
  onResume: () => void;
  onDelete: () => void;
}) {
  const progress = session.totalQuestions > 0
    ? Math.round((session.currentIndex / session.totalQuestions) * 100)
    : 0;
  const remaining = session.totalQuestions - session.currentIndex;
  const barColor = isFlashcard ? 'bg-violet-400' : isQuick ? 'bg-amber-400' : 'bg-teal-400';
  const btnColor = isFlashcard ? 'bg-violet-500' : isQuick ? 'bg-amber-500' : 'bg-squishy-primary';

  return (
    <div className="bg-white border rounded-xl px-3 py-2.5 flex items-center gap-2.5 active:bg-slate-50 transition-colors">
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onResume}>
        <div className="flex items-center gap-2">
          {isFlashcard && (
            <span className="text-[9px] font-bold text-violet-500 bg-violet-50 px-1.5 py-0.5 rounded shrink-0">FC</span>
          )}
          {isQuick && (
            <span className="text-[9px] font-bold text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded shrink-0">QS</span>
          )}
          <span className="text-xs font-medium text-squishy-dark truncate">{session.label || 'All Topics'}</span>
          <span className="text-[10px] text-slate-400 shrink-0">{remaining} left</span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${progress}%` }} />
          </div>
          <span className="text-[10px] text-slate-400 shrink-0 w-7 text-right">{progress}%</span>
        </div>
      </div>
      <button onClick={onResume} className={`${btnColor} text-white px-2.5 py-1 rounded-lg text-[11px] font-semibold shrink-0 active:scale-95 transition-transform`}>
        Resume
      </button>
      <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-slate-300 hover:text-red-400 text-sm shrink-0 transition-colors">
        &#x2715;
      </button>
    </div>
  );
}

// ── Flashcard Review Card ─────────────────────────────────────────────────────

function FlashcardReviewCard({ allFlashcards, createSession, navigate, sectionId }: {
  allFlashcards: any[];
  createSession: ReturnType<typeof useCreateSession>;
  navigate: ReturnType<typeof useNavigate>;
  sectionId: string;
}) {
  const flaggedSet = getFlaggedFlashcardIds();
  const flaggedInContext = allFlashcards.filter(q => flaggedSet.has(q.id));

  if (flaggedInContext.length === 0) return null;

  const handleStudyFlagged = async () => {
    const questionIds = flaggedInContext.map(q => q.id);
    // Shuffle
    for (let i = questionIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = questionIds[i]!;
      questionIds[i] = questionIds[j]!;
      questionIds[j] = tmp;
    }
    const result = await createSession.mutateAsync({
      classSectionId: sectionId,
      filters: { topics: [], mode: 'flashcard' } as any,
      questionIds,
      label: 'Review Flagged Cards',
    });
    navigate(`/flashcards/${sectionId}?session=${result.session.id}`);
  };

  return (
    <button
      onClick={handleStudyFlagged}
      disabled={createSession.isPending}
      className="w-full bg-white border border-violet-200 rounded-2xl p-4 flex items-center gap-3 active:bg-violet-50 transition-colors disabled:opacity-50"
    >
      <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-violet-400">
          <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z" />
        </svg>
      </div>
      <div className="flex-1 text-left">
        <div className="text-sm font-semibold text-squishy-dark">Flagged Flash Cards</div>
        <div className="text-xs text-slate-500">{flaggedInContext.length} card{flaggedInContext.length !== 1 ? 's' : ''} to review</div>
      </div>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400">
        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
      </svg>
    </button>
  );
}

// ── Session Name Field ────────────────────────────────────────────────────────
// Optional custom name so open sessions are distinguishable. Placeholder shows
// the auto-generated default that is used when left blank.

function SessionNameField({ value, onChange, placeholder, accent }: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  accent: string;
}) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Name this session</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, 60))}
        placeholder={placeholder}
        maxLength={60}
        className={`mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 ${accent}`}
      />
    </div>
  );
}

// ── Upload Filter Chips ───────────────────────────────────────────────────────
// Under "My Questions", lets the user narrow a session to content generated
// from specific uploads.

function UploadFilterChips({ uploads, selected, onToggle, countFor, accentClass, showAuthor }: {
  uploads: { id: string; title: string; createdAt: string; questionCount: number; flashcardCount: number; quickCount: number; authorName?: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  countFor: (u: { questionCount: number; flashcardCount: number; quickCount: number }) => number;
  accentClass: string;
  showAuthor?: boolean;
}) {
  const usable = uploads.filter(u => countFor(u) > 0);
  if (usable.length === 0) return null;
  return (
    <div className="mt-2">
      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
        {showAuthor ? "Friends' uploads" : 'From specific uploads'} {selected.size > 0 ? `(${selected.size} selected)` : '(all)'}
      </div>
      <div className="max-h-28 overflow-y-auto space-y-1.5 pr-1">
        {usable.map(u => {
          const isSelected = selected.has(u.id);
          return (
            <button
              key={u.id}
              onClick={() => onToggle(u.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-all active:scale-[0.98] ${
                isSelected ? accentClass : 'border-slate-200 text-slate-600'
              }`}
            >
              <span className="text-xs font-medium truncate">
                {showAuthor && u.authorName ? <span className="text-slate-400">{u.authorName}: </span> : null}
                {u.title}
              </span>
              <span className="text-[10px] text-slate-400 shrink-0 ml-2">
                {countFor(u)} &#xB7; {new Date(u.createdAt).toLocaleDateString()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// 3-way question source tabs: All / Mine / Friends.
function SourceTabs({ value, onChange, counts, accent, mineLabel, friendsLabel, friendsLocked, onLockedFriends }: {
  value: QSource;
  onChange: (s: QSource) => void;
  counts: { all: number; mine: number; friends: number };
  accent: string;
  mineLabel: string;
  friendsLabel: string;
  friendsLocked?: boolean;
  onLockedFriends?: () => void;
}) {
  const tab = (key: QSource, label: string, n: number, locked?: boolean) => (
    <button
      onClick={() => { if (locked) { onLockedFriends?.(); return; } onChange(key); }}
      className={`flex-1 py-1.5 text-[11px] font-semibold rounded-md transition-all inline-flex items-center justify-center gap-1 ${value === key ? `bg-white ${accent} shadow-sm` : 'text-slate-500'} ${locked ? 'opacity-70' : ''}`}
    >
      {locked && (
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-amber-500">
          <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
        </svg>
      )}
      {label} ({n})
    </button>
  );
  return (
    <div className="mt-3 flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
      {tab('all', 'All', counts.all)}
      {tab('mine', mineLabel, counts.mine)}
      {tab('friends', friendsLabel, counts.friends, friendsLocked)}
    </div>
  );
}

function getTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Comments Section ──────────────────────────────────────────────────────────

function CommentsSection({
  classSectionId,
  comments,
  entitlements,
  postComment,
  deleteComment,
}: {
  classSectionId: string;
  comments: ClassComment[];
  entitlements?: { canPost: boolean; canReply: boolean; canSeeReplies: boolean };
  postComment: ReturnType<typeof usePostComment>;
  deleteComment: ReturnType<typeof useDeleteComment>;
}) {
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  const handlePost = () => {
    if (!newComment.trim()) return;
    postComment.mutate({ classSectionId, body: newComment.trim() }, {
      onSuccess: () => setNewComment(''),
    });
  };

  const handleReply = (parentId: string) => {
    if (!replyText.trim()) return;
    postComment.mutate({ classSectionId, parentId, body: replyText.trim() }, {
      onSuccess: () => { setReplyTo(null); setReplyText(''); },
    });
  };

  const handleDelete = (id: string) => {
    deleteComment.mutate({ id, classSectionId });
  };
  const canPost = entitlements?.canPost ?? false;
  const canReply = entitlements?.canReply ?? false;
  const canSeeReplies = entitlements?.canSeeReplies ?? false;

  return (
    <div className="bg-white border rounded-xl overflow-hidden">
      <div className="px-4 pt-3 pb-2">
        <h2 className="font-semibold text-squishy-dark text-sm">Discussion</h2>
      </div>

      {canPost ? (
        <div className="px-4 pb-3 flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePost()}
            placeholder="Write a comment..."
            className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-squishy-primary/30 focus:border-squishy-primary"
          />
          <button
            onClick={handlePost}
            disabled={!newComment.trim() || postComment.isPending}
            className="bg-squishy-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition disabled:opacity-40"
          >
            Post
          </button>
        </div>
      ) : (
        <div className="mx-4 mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          Upgrade to Plus or Pro to join class discussions. Free users can preview the top comment.
        </div>
      )}

      {/* Comments list */}
      <div className="divide-y">
        {comments.length === 0 && (
          <div className="px-4 py-6 text-center text-slate-400 text-sm">
            No comments yet. Start the conversation!
          </div>
        )}
        {comments.map((c) => (
          <div key={c.id} className="px-4 py-3">
            <CommentRow comment={c} onReply={canReply ? () => setReplyTo(replyTo === c.id ? null : c.id) : undefined} onDelete={() => handleDelete(c.id)} />

            {/* Replies */}
            {canSeeReplies && c.replies.length > 0 && (
              <div className="ml-8 mt-2 space-y-2 border-l-2 border-slate-100 pl-3">
                {c.replies.map((r) => (
                  <CommentRow key={r.id} comment={r} isReply onDelete={() => handleDelete(r.id)} />
                ))}
              </div>
            )}

            {/* Reply input */}
            {replyTo === c.id && (
              <div className="ml-8 mt-2 flex gap-2">
                <input
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleReply(c.id)}
                  placeholder="Reply..."
                  autoFocus
                  className="flex-1 border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-squishy-primary/30 focus:border-squishy-primary"
                />
                <button
                  onClick={() => handleReply(c.id)}
                  disabled={!replyText.trim() || postComment.isPending}
                  className="bg-squishy-primary text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:opacity-90 transition disabled:opacity-40"
                >
                  Reply
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CommentRow({
  comment,
  isReply,
  onReply,
  onDelete,
}: {
  comment: ClassComment;
  isReply?: boolean;
  onReply?: () => void;
  onDelete: () => void;
}) {
  const timeAgo = formatTimeAgo(comment.createdAt);

  return (
    <div>
      <div className="flex items-start gap-2">
        <div className={`${isReply ? 'w-6 h-6 text-[9px]' : 'w-7 h-7 text-[10px]'} rounded-full bg-slate-200 overflow-hidden flex items-center justify-center font-medium shrink-0`}>
          {comment.author.avatarUrl
            ? <img src={comment.author.avatarUrl} alt="" className="w-full h-full object-cover" />
            : comment.author.displayName[0]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className={`font-medium ${isReply ? 'text-xs' : 'text-sm'}`}>{comment.author.displayName}</span>
            <span className="text-[10px] text-slate-400">{timeAgo}</span>
          </div>
          <p className={`${isReply ? 'text-xs' : 'text-sm'} text-slate-700 mt-0.5`}>{comment.body}</p>
          <div className="flex gap-3 mt-1">
            {onReply && (
              <button onClick={onReply} className="text-[10px] text-slate-400 hover:text-squishy-primary font-medium">Reply</button>
            )}
            {comment.isOwn && (
              <button onClick={onDelete} className="text-[10px] text-slate-400 hover:text-red-500 font-medium">Delete</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatTimeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}
